/* =========================================================================
 * WHY IS THERE NO CHAMPION? — pure. No React, no I/O.
 *
 * Brian: "you should see the actual championships crowned. You need to do a
 * better job of showing who has won a championship."
 *
 * ⚠️ AN EMPTY CHAMPION FIELD MEANS FIVE OPPOSITE THINGS, AND THEY HAVE ALL
 * RENDERED IDENTICALLY. This is the silent-zero failure in its most expensive
 * form, because the champion is the trigger for the entire sale — a rep cannot
 * call anyone until they know who won:
 *
 *   NOT YET CONTESTED   the event has not happened. A date, not a gap.
 *   WITHDRAWN           a crown was recorded here and pulled. A DECISION. See
 *                       RETRACTED below — this one nearly shipped as "nobody
 *                       has looked yet", which is how a bad guess gets
 *                       re-crowned.
 *   NEVER SEARCHED      queued. A POSITION, not a dead end.
 *   SEARCHED, NOTHING   N attempts and no result. At 3+ the rep should stop
 *                       waiting on a search and call the organisation. Four
 *                       records once sat at 175 attempts each (stated in
 *                       Brian's brief of 2026-10-01; not measured here).
 *   NO RESULT PUBLISHED TERMINAL. Stop them wasting a fifth search.
 *
 * ⚠️ AND IT NEVER CLAIMS A SEARCH THAT DID NOT HAPPEN. `looked` is true only
 * when the record carries a search tag, a non-zero attempt count, or a
 * `champion_searched_at` timestamp — reusing the signals the date-integrity
 * model reads, deliberately, so the two surfaces cannot disagree about whether
 * anyone looked. A false claim of effort is worse than silence; a false claim
 * that NOBODY looked is the same lie pointing the other way, and the timestamp
 * is in there to stop it.
 *
 * ⚠️ TERMINAL IS NEVER INFERRED FROM A COUNT. "We tried a lot" is not "no result
 * exists". Only an explicit tag on the record can retire a championship, because
 * guessing terminal would stop a rep chasing a title that was published all
 * along.
 * ========================================================================= */

import { isMultiSchool, athletesOf, schoolGroups, sameSchoolAlias } from './championDisplay.js'

const S = (v) => (v == null ? '' : String(v)).trim()

/** A tag that says the result does not exist, rather than has not been found. */
const TERMINAL = /\b(no[-_ ]?result|none[-_ ]?published|not[-_ ]?published|unpublished|terminal|no[-_ ]?public[-_ ]?result)\b/i

/**
 * ⚠️ A WITHDRAWN CROWN IS A DECISION, NOT A GAP — and this nearly shipped wrong.
 *
 * `champion_detected_raw` is where `repair-defects` writes the RETRACTION
 * NARRATIVE when a crown is pulled. championships.js says why in as many words:
 * it is written to the record and not only to the ledger "so that a blank
 * champion reads as a DECISION and not as a gap nobody has got to yet. Without
 * it on the record, the row goes straight back into the hunt queue and somebody
 * re-crowns it from the same bad guess."
 *
 * A model that answered "nobody has looked yet" on one of those records would
 * have caused precisely that re-crowning. So prior champion text outranks both
 * never-searched and searched-nothing.
 */
const RETRACTED = /\b(retract|withdraw|withdrew|revok|rescind|reversed|pulled|un[-_ ]?crown|incorrect|wrong\b|bad[-_ ]?guess|disput)/i

/* Escalate to a human call rather than a fifth search. Brian's threshold. */
export const ESCALATE_AFTER_ATTEMPTS = 3

/**
 * THE CHAMPION'S NAME, IN FULL.
 *
 * ⚠️ Brian: "you are surfacing the champion with what looks like a single word
 * or only part of the school name. for example, RUSSELLVILLE — shouldnt that be
 * RUSSELLVILLE HS". Two fields can hold it: `current_champion` and
 * `sr_winning_school`. When one is a fuller form of the other, the fuller one is
 * what a rep needs.
 *
 * ⚠️ NOTHING IS APPENDED. This never adds "HS" or "High School" to a fragment —
 * inventing the rest of an institution's name is fabricating a customer. If the
 * record only holds a fragment, the fragment renders and `partial` is true so
 * the UI can say the stored name looks incomplete, which is a data problem to
 * fix at the source rather than paper over here.
 */
export function championName(c) {
  const champ = S(c?.champion)
  const school = S(c?.winningSchool)
  if (!champ && !school) return { name: '', partial: false, alternate: '' }
  if (!champ) return { name: school, partial: looksPartial(school), alternate: '' }
  if (!school) return { name: champ, partial: looksPartial(champ), alternate: '' }
  /* Only an explicit safe alias permits choosing the fuller form. */
  if (sameSchoolAlias(champ, school) && school.length > champ.length) return { name: school, partial: false, alternate: champ }
  if (sameSchoolAlias(champ, school) && champ.length > school.length) return { name: champ, partial: false, alternate: school }
  /* Genuinely different values. Both are shown; neither is silently dropped. */
  return { name: champ, partial: looksPartial(champ), alternate: school }
}

/** A single bare word, or an all-caps token, reads like a fragment. */
function looksPartial(name) {
  const n = S(name)
  if (!n) return false
  if (/\s/.test(n)) return false
  return n.length > 2
}

/**
 * Every champion on the record, not just one.
 *
 * ⚠️ ONE RECORD CAN CROWN MANY. Wrestling crowns a champion per weight class
 * plus a team title; a horse show crowns ~150 class titles plus 7 grand stakes.
 * A single champion field cannot express that, so a UI that assumes one hides
 * the rest. Separators are the ones the object actually uses.
 */
export function championList(c) {
  const { name, alternate } = championName(c)
  if (!name) return []
  const parts = name.split(/\s*(?:;|\||\n|•)\s*/).map(S).filter(Boolean)
  const list = parts.length > 1 ? parts : [name]
  if (alternate && !sameSchoolAlias(name, alternate) && !list.some((x) => x.toLowerCase() === alternate.toLowerCase())) list.push(alternate)
  return list
}

/**
 * @returns {{
 *   kind: 'crowned'|'not-yet'|'prior-finding'|'never-searched'|'searched-nothing'|'no-result',
 *   headline: string,
 *   detail: string,
 *   attempts: number,
 *   escalate: boolean,
 *   sourced: boolean,
 *   sourceUrl: string,
 *   locked: boolean,
 * }}
 */
export function championState(c, now = new Date()) {
  const { name } = championName(c)
  const attempts = Number(c?.championSearchAttempts || 0) || 0
  const tag = S(c?.championSearchTag)
  /* ⚠️ THREE SIGNALS, NOT TWO. `champion_searched_at` is a timestamp of an
   * actual hunt; a record carrying one with attempts 0 and no tag would
   * otherwise be reported as never looked at, which is a false claim in the
   * opposite direction from the one this module mostly guards against. */
  const looked = Boolean(tag) || attempts > 0 || Boolean(S(c?.championSearchedAt))
  const sourceUrl = S(c?.championSourceUrl) || S(c?.story?.sourceUrl)
  /* A lock is a person's veto. It must be legible BEFORE a control that it
   * would refuse — Brian's rule against a button that looks live and comes
   * back "no". */
  const locked = Boolean(c?.championLocked)
  const base = { attempts, escalate: false, sourced: Boolean(sourceUrl), sourceUrl, locked }

  const athletes = athletesOf(c)
  if (isMultiSchool(c)) {
    const groups = schoolGroups(c)
    return { ...base, kind: 'prior-finding', headline: 'Multi-school — resolution required',
      detail: athletes.ok && groups.ok
        ? 'No single champion is named. ' + groups.groups.map((g) => `${g.school}: ${g.athletes.map((a) => [a.athlete, a.event].filter(Boolean).join(' — ')).join('; ')}`).join(' · ')
        : 'Recorded individual references are unreadable. Review the source; do not treat this as a hunt gap.' }
  }
  if (!athletes.ok) return { ...base, kind: 'prior-finding', headline: 'Individual references unreadable',
    detail: 'Recorded athlete evidence cannot be read. Review and repair it before quoting a winner; this is not an empty hunt result.' }

  if (name) {
    /* ⚠️ A CROWN IS A TRANSACTION: champion + source + who + when. 63 crowns
     * carry no source at all (stated in Brian's brief of 2026-10-01; not
     * measured by this session), and rendering those identically to a sourced
     * one makes an unverifiable claim look like a verified one. */
    return {
      ...base,
      kind: 'crowned',
      headline: name,
      detail: sourceUrl
        ? 'Sourced.'
        : 'Recorded, but with no source on the record — weaker than a sourced crown, and worth confirming before it is quoted to a customer.',
    }
  }

  if (TERMINAL.test(tag)) {
    return {
      ...base,
      kind: 'no-result',
      headline: 'No published result exists',
      detail: 'This has been settled as unpublished rather than unfound. Another search will not '
        + 'produce one — if the winner is needed, it has to come from someone who was there.',
    }
  }

  /* ⚠️ PRIOR CHAMPION TEXT ON AN UNCROWNED RECORD OUTRANKS BOTH "NOBODY LOOKED"
   * AND "SEARCHED, NOTHING FOUND". Somebody wrote an account of a champion here
   * and the champion field is now blank — most often because the crown was
   * withdrawn. Reporting that as an empty queue position is what sends a rep to
   * re-crown it from the same bad guess.
   *
   * ⚠️ AND THE NARRATIVE IS QUOTED, NEVER PARAPHRASED. The regex only decides
   * how confidently the headline reads; the detail is the text a person wrote,
   * verbatim, because summarising someone's retraction note is how the reason a
   * crown was pulled gets lost. */
  const prior = S(c?.championDetectedRaw)
  if (prior) {
    const withdrawn = RETRACTED.test(prior)
    return {
      ...base,
      kind: 'prior-finding',
      headline: withdrawn
        ? 'A crown was recorded here and withdrawn'
        : 'No champion, but there is an account on this record',
      detail: withdrawn
        ? `This is a decision, not a gap — do not re-crown it without reading why it was pulled: “${prior}”`
        : `The champion field is blank while the record carries this: “${prior}”`,
    }
  }

  const date = c?.effDate instanceof Date && !Number.isNaN(c.effDate.getTime()) ? c.effDate : null
  if (date && date.getTime() > now.getTime()) {
    return {
      ...base,
      kind: 'not-yet',
      headline: c?.dateConfidence && c.dateConfidence !== 'confirmed' ? 'Future date — not confirmed' : 'Not yet contested',
      detail: c?.dateConfidence && c.dateConfidence !== 'confirmed'
        ? 'The working date is in the future, but does not establish whether the event has happened. Confirm the event date and result at the source.'
        : 'The recorded event date is in the future: a scheduled date, not a gap by itself. Confirm any conflicting result evidence before treating this as not yet contested.',
    }
  }

  if (!looked) {
    return {
      ...base,
      kind: 'never-searched',
      headline: 'Nobody has looked yet',
      detail: 'This is a position in the queue, not a dead end. No search has been run against '
        + 'this record, so nothing has been ruled out.',
    }
  }

  return {
    ...base,
    kind: 'searched-nothing',
    escalate: attempts >= ESCALATE_AFTER_ATTEMPTS,
    headline: attempts === 1
      ? 'Searched once, nothing found'
      : `Searched ${attempts.toLocaleString()} times, nothing found`,
    detail: attempts >= ESCALATE_AFTER_ATTEMPTS
      ? 'Stop searching and contact the organisation directly. Past three attempts another search '
        + 'is very unlikely to find what the first three did not.'
      : 'A result may still be published, or may need a different source.',
  }
}

/**
 * THE FIELD — RECORDED, AND ONLY SOMETIMES AN OPPORTUNITY.
 *
 * ⚠️ CORRECTED 2026-10-02. This shipped in 2.84.0 under "LOSING TEAMS BUY RINGS —
 * a title is 1 customer, a bracket is 64", promoted on every crowned record.
 * That rationale was withdrawn by Brian the same day:
 *
 *   "That is truly only relevant for big time opportunities. Normally runner
 *    ups aren't getting them."
 *
 * At an ordinary state high-school final THE RUNNER-UP DOES NOT BUY. Promoting
 * the field everywhere sends reps after calls that do not convert, which is
 * worse than not surfacing it at all: a UI that manufactures opportunities
 * costs more than one that omits them. The measured big case — NCA 2026, 40
 * distinct buying teams off one event, one program buying for eight squads — is
 * an all-star cheer mega-event, not a state final.
 *
 * ⚠️ SO THE DATA IS KEPT AND THE CLAIM IS DROPPED. This still returns everything
 * the record holds; nothing is hidden. What changed is that a caller must ask
 * `fieldEmphasis()` before presenting any of it as a lead.
 */
export function championField(c) {
  const out = []
  const { name } = championName(c)
  if (name) out.push({ place: 'Champion', who: name })
  const runnerUp = S(c?.story?.runnerUp) || S(c?.championStory?.runnerUp)
  if (runnerUp) out.push({ place: 'Runner-up', who: runnerUp })
  return out
}

/**
 * IS THIS EVENT BIG ENOUGH THAT THE FIELD IS WORTH CALLING?
 *
 * ⚠️ THE ANSWER COMES FROM MEASURED ORDER HISTORY, NEVER FROM THIS FILE.
 * Brian: "'Big' must come from the data, not from me." The engine measuring
 * buyers per championship event from real orders had not landed when this was
 * written. This CONSUMES it; it does not stand in for it.
 *
 * ⚠️ AND IT WILL NOT INVENT THE CUT-OFF EITHER. Deciding "10 buyers is big" here
 * would hard-code the exact judgement that was asked to come from the
 * distribution — the same mistake as the rationale this replaces, one level
 * down. So a bare count with no measured cut-off beside it is reported as a
 * fact and still left secondary. Three answers:
 *
 *   'measured-many'  the data says this event draws multiple buyers → promote
 *   'measured-few'   the data says it does not → secondary, and say so
 *   'unmeasured'     nobody has measured this event → secondary, and say THAT
 *
 * ⚠️ UNMEASURED IS NOT "SMALL". It is the honest default: secondary, because the
 * known pattern is that runners-up do not buy, but never reported as a finding,
 * because nobody has looked at this event. Exactly the never-searched vs
 * no-result distinction this module already makes about the champion.
 */
/* ⚠️ THESE THREE KEY NAMES ARE THE CLIENT'S GUESS AND MUST BE CONFIRMED.
 *
 *     c.eventBuyers           number  — distinct buying teams at this event
 *     c.eventBuyerTier        string  — 'many' | 'multi' | 'multi-buyer' | 'few' | 'single' | 'one'
 *     c.eventBuyerThreshold   number  — the measured cut-off, sent WITH the count
 *
 * The measuring engine had not shipped when this was written, so nothing here
 * has been matched against a real payload. ⚠️ IF THE ENGINE NAMES THEM
 * DIFFERENTLY THIS FUNCTION SILENTLY RETURNS 'unmeasured' FOREVER and no event
 * is ever promoted — the sent-and-dropped defect this codebase has now hit seven
 * times, pre-committed. It fails safe (secondary, never a false opening) rather
 * than loud, which is the right failure but an easy one to not notice.
 *
 * So: when the engine lands, confirm these three names against its payload and
 * add them to check-contract-fields.mjs. They are deliberately NOT in that
 * contract yet — the contract states what the API PROMISES, and putting a
 * guessed name in it would make the gate assert a promise nobody made. */
export function fieldEmphasis(c) {
  const buyers = Number(c?.eventBuyers)
  const hasCount = Number.isFinite(buyers) && buyers > 0
  /* A classification the measuring engine assigns. Preferred over any count,
   * because it carries the engine's own cut-off instead of one invented here. */
  const tier = S(c?.eventBuyerTier).toLowerCase()
  /* A cut-off supplied WITH the data is the only cut-off that may promote. */
  const cut = Number(c?.eventBuyerThreshold)
  const hasCut = Number.isFinite(cut) && cut > 0

  if (tier === 'many' || tier === 'multi' || tier === 'multi-buyer') {
    return {
      kind: 'measured-many', buyers: hasCount ? buyers : null, promote: true,
      note: hasCount
        ? `Measured from order history: ${buyers.toLocaleString()} distinct buying teams at this event. The field is worth working here, not only the champion.`
        : 'Measured from order history as a multi-buyer event. The field is worth working here, not only the champion.',
    }
  }
  if (tier === 'few' || tier === 'single' || tier === 'one') {
    return {
      kind: 'measured-few', buyers: hasCount ? buyers : null, promote: false,
      note: 'Measured from order history: this event has not produced buyers beyond the champion. Recorded for reference, not as an opening.',
    }
  }
  if (hasCount && hasCut) {
    const many = buyers >= cut
    return {
      kind: many ? 'measured-many' : 'measured-few', buyers, promote: many,
      note: many
        ? `Measured from order history: ${buyers.toLocaleString()} distinct buying teams, against a measured cut-off of ${cut.toLocaleString()}. The field is worth working here.`
        : `Measured from order history: ${buyers.toLocaleString()} distinct buying teams, below the measured cut-off of ${cut.toLocaleString()}. Recorded for reference, not as an opening.`,
    }
  }
  if (hasCount) {
    return {
      kind: 'unmeasured', buyers, promote: false,
      note: `${buyers.toLocaleString()} distinct buying teams recorded at this event, but no measured cut-off has been published yet — so this is not being called big on the strength of a number alone.`,
    }
  }
  return {
    kind: 'unmeasured', buyers: null, promote: false,
    note: 'Buyers per event have not been measured for this championship yet. Kept for reference — at most levels the runner-up does not buy.',
  }
}
